import { useState, useMemo } from 'react'
import TableSortLabel from '@mui/material/TableSortLabel'

const contentBlock = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12
}

const scrollWrap = {
  width: '100%',
  overflowX: 'auto',
  background: '#fff',
  border: '1px solid #d4d4d4',
  borderRadius: 10
}

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  minWidth: 700
}

const thStyle = {
  textAlign: 'left',
  background: '#c8e1e1',
  borderBottom: '1px solid #b4d6cb',
  padding: '10px 12px',
  fontSize: 14,
  whiteSpace: 'nowrap'
}

const tdStyle = {
  padding: '9px 12px',
  borderBottom: '1px solid #ececec',
  fontSize: 14
}

const normalize = (value) => String(value ?? '').trim().toLowerCase()

const StatsVehiclesModal = ({ stats, t, fmtNum, filterCriteria = {} }) => {
  const [orderBy, setOrderBy] = useState('name')
  const [order, setOrder] = useState('asc')

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setOrderBy(col)
      setOrder('asc')
    }
  }

  const filteredVehicles = useMemo(() => {
    const items = Array.isArray(stats?.vehiclesStats) ? stats.vehiclesStats : []

    const selectedVehicleId = normalize(
      filterCriteria?.vehicleId ??
      filterCriteria?.vehicle ??
      ''
    )

    const selectedVehicleName = normalize(
      filterCriteria?.vehicleName ??
      filterCriteria?.name ??
      ''
    )

    return items.filter((vehicle) => {
      const vehicleId = normalize(vehicle?.id)
      const vehicleName = normalize(vehicle?.name)
      const vehicleLicense = normalize(vehicle?.license)

      let match = true

      if (selectedVehicleId) {
        match = vehicleId === selectedVehicleId
      }

      if (selectedVehicleName) {
        match =
          vehicleName === selectedVehicleName ||
          vehicleLicense === selectedVehicleName
      }

      if (!match) return false

      const hasData =
        Number(vehicle.trips) > 0 ||
        Number(vehicle.km) > 0 ||
        Number(vehicle.totalCost) > 0 ||
        Number(vehicle.fuel) > 0

      return hasData
    })
  }, [stats?.vehiclesStats, filterCriteria])

  const sortedVehicles = useMemo(() => {
    return [...filteredVehicles].sort((a, b) => {
      let aVal = a?.[orderBy]
      let bVal = b?.[orderBy]

      if (
        orderBy === 'trips' ||
        orderBy === 'km' ||
        orderBy === 'totalCost' ||
        orderBy === 'avgConsumption' ||
        orderBy === 'fuel'
      ) {
        aVal = Number(aVal) || 0
        bVal = Number(bVal) || 0
      } else {
        aVal = String(aVal ?? '').toLowerCase()
        bVal = String(bVal ?? '').toLowerCase()
      }

      if (aVal > bVal) return order === 'asc' ? 1 : -1
      if (aVal < bVal) return order === 'asc' ? -1 : 1
      return 0
    })
  }, [filteredVehicles, orderBy, order])

  return (
    <div style={contentBlock}>
      <div style={scrollWrap}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  {t('TRIPLOG.STATISTICS.VEHICLES.VEHICLE_NAME')}
                </TableSortLabel>
              </th>

              <th style={thStyle}>
                <TableSortLabel
                  active={orderBy === 'license'}
                  direction={orderBy === 'license' ? order : 'asc'}
                  onClick={() => handleSort('license')}
                >
                  {t('TRIPLOG.STATISTICS.VEHICLES.LICENSE_PLATE')}
                </TableSortLabel>
              </th>

              <th style={thStyle}>
                <TableSortLabel
                  active={orderBy === 'trips'}
                  direction={orderBy === 'trips' ? order : 'asc'}
                  onClick={() => handleSort('trips')}
                >
                  {t('TRIPLOG.STATISTICS.SUMMARY.TOTAL_TRIPS')}
                </TableSortLabel>
              </th>

              <th style={thStyle}>
                <TableSortLabel
                  active={orderBy === 'km'}
                  direction={orderBy === 'km' ? order : 'asc'}
                  onClick={() => handleSort('km')}
                >
                  {t('TRIPLOG.STATISTICS.VEHICLES.KM')}
                </TableSortLabel>
              </th>

              <th style={thStyle}>
                <TableSortLabel
                  active={orderBy === 'totalCost'}
                  direction={orderBy === 'totalCost' ? order : 'asc'}
                  onClick={() => handleSort('totalCost')}
                >
                  {t('TRIPLOG.STATISTICS.VEHICLES.COST')}
                </TableSortLabel>
              </th>

              <th style={thStyle}>
                <TableSortLabel
                  active={orderBy === 'avgConsumption'}
                  direction={orderBy === 'avgConsumption' ? order : 'asc'}
                  onClick={() => handleSort('avgConsumption')}
                >
                  {t('TRIPLOG.STATISTICS.VEHICLES.CONSUMPTION')}
                </TableSortLabel>
              </th>

              <th style={thStyle}>
                <TableSortLabel
                  active={orderBy === 'fuel'}
                  direction={orderBy === 'fuel' ? order : 'asc'}
                  onClick={() => handleSort('fuel')}
                >
                  {t('TRIPLOG.STATISTICS.SUMMARY.TOTAL_FUEL')}
                </TableSortLabel>
              </th>
            </tr>
          </thead>

          <tbody>
            {sortedVehicles.map((vehicle) => (
              <tr key={vehicle.id}>
                <td style={tdStyle}>{vehicle.name}</td>
                <td style={tdStyle}>{vehicle.license}</td>
                <td style={tdStyle}>{fmtNum(vehicle.trips)}</td>
                <td style={tdStyle}>
                  {fmtNum(vehicle.km, 2)} {t('TRIPLOG.UNITS.KM')}
                </td>
                <td style={tdStyle}>
                  {fmtNum(vehicle.totalCost, 2)} {t('TRIPLOG.UNITS.EUR')}
                </td>
                <td style={tdStyle}>
                  {fmtNum(vehicle.avgConsumption, 2)} {t('TRIPLOG.UNITS.L_PER_100KM')}
                </td>
                <td style={tdStyle}>
                  {fmtNum(vehicle.fuel, 2)} {t('TRIPLOG.UNITS.L')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default StatsVehiclesModal